import React, { useContext } from 'react'
import { ComponentConfig, renderContext, usePuck } from '@puckeditor/core'
import { ColorField } from 'breaktime-theme/src/puck/fields/ColorField'
import ProgressBar from 'breaktime-theme/src/steps/shared/ProgressBar'

export type PollProgressBarProps = {
  activeColor?: string
  inactiveColor?: string
  inactiveOpacity?: number
  dotStyle?: 'filled' | 'outlined'
  shadow?: 'subtle' | 'none'
  size?: string
}

export const POLL_PROGRESS_BAR_DEFAULT_PROPS: PollProgressBarProps = {
  activeColor: '#FFFFFF',
  inactiveColor: '#FFFFFF',
  inactiveOpacity: 0.4,
  dotStyle: 'filled',
  shadow: 'subtle',
  size: '13px',
}

export const PuckPollProgressBar: ComponentConfig<PollProgressBarProps> = {
  label: 'Poll Progress Bar',
  defaultProps: POLL_PROGRESS_BAR_DEFAULT_PROPS,
  permissions: {
    drag: false,
    duplicate: false,
    delete: false,
  },
  fields: {
    activeColor: {
      type: 'custom',
      label: 'Active Dot Color',
      render: (props: any) => <ColorField {...props} />,
    },
    inactiveColor: {
      type: 'custom',
      label: 'Inactive Dot Color',
      render: (props: any) => <ColorField {...props} />,
    },
    inactiveOpacity: {
      type: 'number',
      label: 'Inactive Opacity (0-1)',
      min: 0,
      max: 1,
    },
    dotStyle: {
      type: 'radio',
      label: 'Dot Style',
      options: [
        { label: 'Filled', value: 'filled' },
        { label: 'Outlined', value: 'outlined' },
      ],
    },
    shadow: {
      type: 'radio',
      label: 'Shadow',
      options: [
        { label: 'Subtle', value: 'subtle' },
        { label: 'None', value: 'none' },
      ],
    },
    size: { type: 'text', label: 'Dot Size', placeholder: '13px' },
  },
  render: props => <PollProgressBarRender {...(props as PollProgressBarProps)} />,
}

export function progressBarPropsFromConfig(p: PollProgressBarProps) {
  return {
    activeColor: p.activeColor || undefined,
    inactiveColor: p.inactiveColor || undefined,
    inactiveOpacity:
      typeof p.inactiveOpacity === 'number' ? p.inactiveOpacity : undefined,
    dotSize: p.size || undefined,
    outlined: p.dotStyle === 'outlined',
    subtleShadow: p.shadow !== 'none',
  }
}

function PollProgressBarRender(props: PollProgressBarProps) {
  const puckCtx = useContext(renderContext)
  const isRenderMode = puckCtx.data.content.length > 0

  // In production, PollItem owns the real progress bar; this puck component is
  // a config carrier only. PollContainer extracts props from the slot and
  // forwards them to PollItem via puckProgressBarProps.
  if (isRenderMode) return null

  return <PollProgressBarEditorPreview {...props} />
}

function PollProgressBarEditorPreview(props: PollProgressBarProps) {
  const puck = usePuck()
  const root = (puck?.appState?.data?.content ?? []) as any[]
  const container = root.find((c: any) => c.type === 'PollContainer') as any
  const questionCount = Math.max(
    1,
    (container?.props?.questions ?? []).filter((q: any) => q.type === 'PollQuestion').length || 1,
  )

  const styleProps = progressBarPropsFromConfig(props)

  return (
    <ProgressBar
      label="Poll"
      position={1}
      total={questionCount}
      showDots
      showNumber
      {...styleProps}
    />
  )
}
